import React, { useState, useCallback } from 'react';
import {
    Modal,
    Box,
    SpaceBetween,
    Button,
    Tabs,
    SelectProps,
    Flashbar,
    FlashbarProps,
    Spinner,
} from '@amzn/awsui-components-console';
import { v4 as uuidv4 } from 'uuid';

import { uploadFlashbarItemsToS3, uploadReleaseNotesToS3, uploadFlashbarTemplatesToS3 } from 'src/utils/S3Upload';
import { fetchFlashbarTemplates } from 'src/services/flashbarService';
import { FlashbarTemplate } from '../FlashbarForm/FlashbarFormTemplates';
import { releaseNotesBucket } from 'src/config';
import { useAuthContext } from 'src/providers/AuthContext';
import { useGlobalAlertStore } from 'src/stores/globalAlertStore/globalAlertStore';
import { logger } from 'src/utils/logger';
import { isValidUrl } from 'src/utils/StringUtils';

import { FlashbarType, FlashbarItem, FeatureType } from '../FeatureType';
import { ReleaseNote, ReleaseFeature } from '../WhatsNew';
import { FeatureForm } from '../FeatureForm/FeatureForm';
import { FlashbarForm } from '../FlashbarForm/FlashbarForm';

interface AddContentModalProps {
    visible: boolean;
    onDismiss: () => void;
    onSubmitRelease: (releaseData: ReleaseNote) => void;
    onSubmitFlashbar: () => void;
    editingRelease?: ReleaseNote | null;
}

interface FeatureFormData {
    title: string;
    description: string;
    type: FeatureType;
    isEditing: boolean;
}

const AddContentModal: React.FC<AddContentModalProps> = ({
                                                             visible,
                                                             onDismiss,
                                                             onSubmitRelease,
                                                             onSubmitFlashbar,
                                                             editingRelease,
                                                         }) => {
    const { userName } = useAuthContext();
    const { refresh: refreshGlobalAlerts } = useGlobalAlertStore();
    const [activeTabId, setActiveTabId] = useState('features');
    const [isUploading, setIsUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<FlashbarProps.MessageDefinition[]>([]);

    const now = new Date();
    const [releaseYear, setReleaseYear] = useState(String(now.getFullYear()));
    const [releaseMonth, setReleaseMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
    const [highlights, setHighlights] = useState('');
    const [learnMoreLink, setLearnMoreLink] = useState('');
    const [features, setFeatures] = useState<FeatureFormData[]>([
        { title: '', description: '', type: FeatureType.NEW, isEditing: false },
    ]);

    const [type, setType] = useState<SelectProps.Option>({ label: 'Info', value: FlashbarType.INFO });
    const [header, setHeader] = useState('');
    const [content, setContent] = useState('');
    const [isGlobal, setIsGlobal] = useState(true);
    const [isBlockingExecution, setIsBlockingExecution] = useState(false);
    const [expiresAt, setExpiresAt] = useState('');
    const [templates, setTemplates] = useState<FlashbarTemplate[]>([]);

    const resetForm = useCallback(() => {
        setActiveTabId('features');
        const now = new Date();
        setReleaseYear(String(now.getFullYear()));
        setReleaseMonth(String(now.getMonth() + 1).padStart(2, '0'));
        setHighlights('');
        setLearnMoreLink('');
        setFeatures([{ title: '', description: '', type: FeatureType.NEW, isEditing: false }]);
        setType({ label: 'Info', value: FlashbarType.INFO });
        setHeader('');
        setContent('');
        setIsGlobal(true);
        setIsBlockingExecution(false);
        setExpiresAt('');
        setErrorMessage([]);
    }, []);

    React.useEffect(() => {
        fetchFlashbarTemplates().then(setTemplates);
    }, []);

    React.useEffect(() => {
        if (editingRelease && visible) {
            const [year, month] = editingRelease.version.split('.');
            setReleaseYear(year);
            setReleaseMonth(month);
            setHighlights(editingRelease.highlights.map((highlight, index) => `${index + 1}. ${highlight}`).join('\n'));
            setLearnMoreLink(editingRelease.learnMoreLink || '');
            setFeatures(editingRelease.features.map(feature => ({
                title: feature.title,
                description: feature.description,
                type: feature.type,
                isEditing: false,
            })));
            setActiveTabId('features');
            setErrorMessage([]);
        } else if (!editingRelease && visible) {
            resetForm();
        }
    }, [editingRelease, visible, resetForm]);

    const updateFeature = (index: number, field: keyof FeatureFormData, value: string | FeatureType | boolean) => {
        const updated = [...features];
        updated[index] = { ...updated[index], [field]: value };
        setFeatures(updated);
    };

    const addFeature = () => {
        setFeatures([...features, { title: '', description: '', type: FeatureType.NEW, isEditing: true }]);
    };

    const removeFeature = (index: number) => {
        if (features.length > 1) {
            setFeatures(features.filter((_, featureIndex) => featureIndex !== index));
        }
    };

    const confirmEdit = (index: number) => {
        const feature = features[index];
        if (feature.title && feature.description) {
            updateFeature(index, 'isEditing', false);
        }
    };

    const handleSubmit = async () => {
        setIsUploading(true);
        try {
            if (activeTabId === 'features') {
                const version = `${releaseYear}.${releaseMonth}`;
                const date = new Date(parseInt(releaseYear), parseInt(releaseMonth) - 1).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                });

                const validHighlights = highlights
                    .split('\n')
                    .map((line) => line.trim().replace(/^\d+\.\s*/, ''))
                    .filter(Boolean);

                const validFeatures: ReleaseFeature[] = features
                    .filter(({ title, description }) => title && description)
                    .map(({ title, description, type }) => ({ title, description, type }));

                const releaseData: ReleaseNote = {
                    version,
                    date,
                    highlights: validHighlights,
                    features: validFeatures,
                    ...(learnMoreLink && { learnMoreLink }),
                };

                await uploadReleaseNotesToS3(releaseData, parseInt(releaseYear), parseInt(releaseMonth), releaseNotesBucket);
                onSubmitRelease(releaseData);
            } else {
                const newItem: FlashbarItem = {
                    type: type.value as FlashbarType,
                    header,
                    content,
                    dismissible: !isBlockingExecution,
                    isGlobal,
                    id: uuidv4(),
                    isBlockingExecution,
                    createdBy: userName || 'unknown',
                    createdAt: new Date().toISOString(),
                    ...(isBlockingExecution && { overrideBindle: 'snow_team_internal' }),
                    ...(expiresAt && { expiresAt: new Date(expiresAt).toISOString() }),
                };
                await uploadFlashbarItemsToS3(newItem, releaseNotesBucket);

                if (isGlobal) {
                    await refreshGlobalAlerts();
                }

                onSubmitFlashbar();
            }
            resetForm();
        } catch (error) {
            logger.error(`Failed to upload content [ activeTab : error ] ::: [ ${activeTabId} : ${JSON.stringify(error)} ]`);
            setErrorMessage([{
                type: 'error',
                content: 'Failed to upload. Please try again.',
                dismissible: true,
                onDismiss: () => setErrorMessage([]),
            }]);
        } finally {
            setIsUploading(false);
        }
    };

    const isValidFeatureSet = features.length > 0
        && features.every(({ title, description }) => title && description)
        && (!learnMoreLink.trim() || isValidUrl(learnMoreLink));

    const expiresAtError = (() => {
        if (!expiresAt) {
            return undefined;
        }
        const parsed = new Date(expiresAt);
        if (Number.isNaN(parsed.getTime())) {
            return 'Enter a valid expiry date and time.';
        }
        if (parsed.getTime() <= Date.now()) {
            return 'Expiry date and time must be in the future.';
        }
        return undefined;
    })();

    const isFormValid = activeTabId === 'features'
        ? isValidFeatureSet
        : Boolean(header.trim() && content.trim() && !expiresAtError);

    return (
        <Modal
            visible={visible}
            onDismiss={() => {
                resetForm();
                onDismiss();
            }}
            header={editingRelease ? 'Edit Release' : 'Add New Content'}
            size="large"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        {isUploading && (
                            <Box display="inline-block">
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Spinner />
                                    <Box variant="span">Uploading...</Box>
                                </SpaceBetween>
                            </Box>
                        )}
                        <Button variant="link" onClick={onDismiss} disabled={isUploading}>
                            Cancel
                        </Button>
                        <Button
                            data-testid="add-content-submit-button"
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={!isFormValid || isUploading}
                        >
                            Submit
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            {errorMessage.length > 0 && <Flashbar items={errorMessage} />}
            <Box>
                <Tabs
                    activeTabId={activeTabId}
                    onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
                    tabs={[
                        {
                            id: 'features',
                            label: 'Features',
                            content: (
                                <FeatureForm
                                    releaseYear={releaseYear}
                                    releaseMonth={releaseMonth}
                                    highlights={highlights}
                                    learnMoreLink={learnMoreLink}
                                    features={features}
                                    onReleaseYearChange={setReleaseYear}
                                    onReleaseMonthChange={setReleaseMonth}
                                    onHighlightsChange={setHighlights}
                                    onLearnMoreLinkChange={setLearnMoreLink}
                                    onUpdateFeature={updateFeature}
                                    onAddFeature={addFeature}
                                    onRemoveFeature={removeFeature}
                                    onConfirmEdit={confirmEdit}
                                    isEditMode={!!editingRelease}
                                />
                            ),
                        },
                        {
                            id: 'flashbar',
                            label: 'Flashbar Items',
                            content: (
                                <FlashbarForm
                                    onSaveAsTemplate={async (template) => {
                                        const newTemplate: FlashbarTemplate = { ...template, id: uuidv4() };
                                        const updatedTemplates = [...templates, newTemplate];
                                        await uploadFlashbarTemplatesToS3(updatedTemplates, releaseNotesBucket);
                                        setTemplates(updatedTemplates);
                                    }}
                                    templates={templates}
                                    onTemplateSelect={(template: FlashbarTemplate) => {
                                        setType({ label: template.type.charAt(0).toUpperCase() + template.type.slice(1), value: template.type });
                                        setHeader(template.header);
                                        setContent(template.content);
                                        setIsGlobal(template.isGlobal);
                                        setIsBlockingExecution(template.isBlockingExecution);
                                    }}
                                    type={type}
                                    header={header}
                                    content={content}
                                    isGlobal={isGlobal}
                                    isBlockingExecution={isBlockingExecution}
                                    expiresAt={expiresAt}
                                    expiresAtError={expiresAtError}
                                    onTypeChange={setType}
                                    onGlobalChange={setIsGlobal}
                                    onHeaderChange={setHeader}
                                    onContentChange={setContent}
                                    onBlockingExecutionChange={setIsBlockingExecution}
                                    onExpiresAtChange={setExpiresAt}
                                />
                            ),
                        },
                    ]}
                />
            </Box>
        </Modal>
    );
};

export default AddContentModal;
